import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import {
  ChartGridLine,
  ChartModel,
  ChartPoint,
  ChartXLabel,
  DateRange,
  DonutSegment,
  IDeliveryNoteStats,
  IDeliveryNoteTimeline,
  PeriodMode,
  SummaryRow,
  TimelineMetric,
} from '../../shared/models/statistics.model';
import { StatisticService } from '../../shared/services/statistic.service';

const DONUT_CIRCUMFERENCE = 2 * Math.PI * 80;
const DONUT_COLORS = ['#2e73f7', '#8ebeff', '#22c55e', '#eab308'];
const DONUT_REST_COLOR = '#9fb0c9';

// --- Timeline chart geometry (SVG user units) ---
const CHART_W = 800;
const CHART_H = 300;
const CHART_PAD = { top: 16, right: 20, bottom: 28, left: 56 };
const CHART_PLOT_W = CHART_W - CHART_PAD.left - CHART_PAD.right;
const CHART_PLOT_H = CHART_H - CHART_PAD.top - CHART_PAD.bottom;
const CHART_BASELINE = CHART_PAD.top + CHART_PLOT_H;

@Component({
  selector: 'app-deliver-note-statistics',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, DatePickerModule],
  templateUrl: './deliver-note-statistics.component.html',
  styleUrls: ['./deliver-note-statistics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliverNoteStatisticsComponent {
  private statisticService = inject(StatisticService);

  public readonly periodModes: { key: PeriodMode; label: string }[] = [
    { key: 'day', label: 'Tag' },
    { key: 'month', label: 'Monat' },
    { key: 'year', label: 'Jahr' },
    { key: 'custom', label: 'Benutzerdefiniert' },
  ];
  public readonly donutMetricOptions = [
    { label: 'Gesamtstückzahl', value: 'pieces' },
    { label: 'Anzahl Lieferzettel', value: 'count' },
  ];
  public readonly timelineMetricOptions: { label: string; value: TimelineMetric; color: string }[] = [
    { label: 'Lieferzettel', value: 'deliveryNoteCount', color: '#2e73f7' },
    { label: 'Positionen', value: 'positionCount', color: '#8ebeff' },
    { label: 'Kartons', value: 'cartonCount', color: '#eab308' },
    { label: 'Folien', value: 'foilTotal', color: '#22c55e' },
    { label: 'Umkartons', value: 'umCartons', color: '#2dd4bf' },
    { label: 'Gewicht (kg)', value: 'weightKg', color: '#a78bfa' },
  ];
  public readonly summaryColumns = [
    'Kennzahl',
    'Rollen',
    "Stck a'",
    'Gesamt Stk.',
    'Folie 1',
    'Folie A',
    'Folie N',
    'Dry Packs',
    'Ammo Packs',
    'Umkartons',
    'Kt.',
    'kg',
    'Durchschn. pro LZ',
  ];

  // --- State signals ---
  public readonly mode = signal<PeriodMode>('month');
  public readonly customRange = signal<Date[] | null>(null);
  public readonly donutMetric = signal<'count' | 'pieces'>('pieces');
  public readonly timelineMetric = signal<TimelineMetric>('deliveryNoteCount');
  private readonly anchor = signal(new Date());

  // --- Selected range (pure derivation, null while a custom range is incomplete) ---
  public readonly range = computed<DateRange | null>(() => this.computeRange(this.mode(), this.anchor(), this.customRange()));

  // --- Data fetch: refetches automatically whenever `range()` changes ---
  private readonly statsResource = rxResource({
    params: () => this.range() ?? undefined,
    stream: ({ params }) => this.statisticService.getDeliveryNoteStats(params.from, params.to),
  });

  public readonly isLoading = this.statsResource.isLoading;
  public readonly errorMessage = computed(() =>
    this.statsResource.error() || this.timelineResource.error() ? 'Statistiken konnten nicht geladen werden.' : ''
  );
  private readonly statsData = computed<IDeliveryNoteStats | null>(() => this.statsResource.value()?.DATA ?? null);

  // --- Derived views ---
  public readonly rangeLabel = computed(() => {
    const r = this.range();
    if (!r) return '';
    const from = r.from.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    if (this.mode() === 'day') return from;
    const to = r.to.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${from} – ${to}`;
  });

  public readonly lastUpdatedLabel = computed(() => {
    const stats = this.statsData();
    if (!stats) return '—';
    const d = new Date(stats.generatedAt);
    return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  });

  public readonly kpis = computed(() => {
    const stats = this.statsData();
    if (!stats) return [];
    const t = stats.totals;
    return [
      { title: 'Anzahl Lieferzettel', value: t.deliveryNoteCount.toLocaleString('de-DE'), icon: 'description' },
      { title: 'Positionen', value: t.positionCount.toLocaleString('de-DE'), icon: 'format_list_numbered' },
      { title: 'Kartons', value: t.cartonCount.toLocaleString('de-DE'), icon: 'widgets' },
      { title: 'Folien', value: t.foilTotal.toLocaleString('de-DE'), icon: 'layers' },
      { title: 'Gewicht', value: this.formatKg(t.weightKg), icon: 'monitor_weight' },
      { title: 'Umkartons', value: t.umCartons.toLocaleString('de-DE'), icon: 'inventory_2' },
    ];
  });

  public readonly donutCenterLabel = computed(() => (this.donutMetric() === 'count' ? 'Lieferzettel' : 'Stück'));

  private readonly donut = computed(() => {
    const empty = { segments: [] as DonutSegment[], total: '' };
    const stats = this.statsData();
    if (!stats) return empty;

    const metric = this.donutMetric();
    const metricOf = (c: { deliveryNoteCount: number; totalPieces: number }) => (metric === 'count' ? c.deliveryNoteCount : c.totalPieces);

    const customers = [...stats.byCustomer].sort((a, b) => metricOf(b) - metricOf(a));
    const total = customers.reduce((sum, c) => sum + metricOf(c), 0);
    const totalLabel = total.toLocaleString('de-DE');
    if (total <= 0) return { segments: [], total: totalLabel };

    const top = customers.slice(0, DONUT_COLORS.length);
    const rest = customers.slice(DONUT_COLORS.length);
    const entries: { label: string; value: number; color: string }[] = top.map((c, i) => ({
      label: c.companyName,
      value: metricOf(c),
      color: DONUT_COLORS[i],
    }));
    if (rest.length > 0) {
      entries.push({
        label: 'Weitere',
        value: rest.reduce((sum, c) => sum + metricOf(c), 0),
        color: DONUT_REST_COLOR,
      });
    }

    let acc = 0;
    const segments = entries
      .filter(e => e.value > 0)
      .map(e => {
        const fraction = e.value / total;
        const segment: DonutSegment = {
          label: e.label,
          value: e.value,
          valueLabel: e.value.toLocaleString('de-DE'),
          percent: Math.round(fraction * 100),
          color: e.color,
          dashArray: `${fraction * DONUT_CIRCUMFERENCE} ${DONUT_CIRCUMFERENCE}`,
          dashOffset: -acc * DONUT_CIRCUMFERENCE,
        };
        acc += fraction;
        return segment;
      });
    return { segments, total: totalLabel };
  });

  public readonly donutSegments = computed(() => this.donut().segments);
  public readonly donutTotal = computed(() => this.donut().total);

  // --- Timeline chart: independent resource keyed off the same selected range ---
  private readonly timelineResource = rxResource({
    params: () => this.range() ?? undefined,
    stream: ({ params }) => this.statisticService.getDeliveryNoteTimeline(params.from, params.to),
  });

  public readonly timelineLoading = this.timelineResource.isLoading;
  private readonly timelineData = computed<IDeliveryNoteTimeline | null>(() => this.timelineResource.value()?.DATA ?? null);

  public readonly chart = computed<ChartModel>(() => {
    const empty: ChartModel = {
      hasData: false,
      linePath: '',
      areaPath: '',
      points: [],
      showMarkers: false,
      gridLines: [],
      xLabels: [],
      color: '#2e73f7',
    };
    const data = this.timelineData();
    if (!data) return empty;

    const metric = this.timelineMetric();
    const color = this.timelineMetricOptions.find(o => o.value === metric)?.color ?? '#2e73f7';
    // Hourly view (1–2 day ranges) keeps zero buckets so the axis spans the full day;
    // coarser views drop empty buckets so idle days/weekends don't flatten the graph.
    const buckets = data.granularity === 'hour' ? data.buckets : data.buckets.filter(b => b[metric] > 0);
    if (!buckets.some(b => b[metric] > 0)) return { ...empty, color };

    const values = buckets.map(b => b[metric]);
    const dataMax = Math.max(...values, 0);
    const scale = this.niceScale(dataMax, 4);

    const n = buckets.length;
    const xAt = (i: number) => (n === 1 ? CHART_PAD.left + CHART_PLOT_W / 2 : CHART_PAD.left + (i / (n - 1)) * CHART_PLOT_W);
    const yAt = (v: number) => CHART_PAD.top + CHART_PLOT_H - (v / scale.niceMax) * CHART_PLOT_H;

    const points: ChartPoint[] = values.map((v, i) => ({ x: this.round(xAt(i)), y: this.round(yAt(v)) }));
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaPath = points.length ? `${linePath} L ${points[points.length - 1].x} ${CHART_BASELINE} L ${points[0].x} ${CHART_BASELINE} Z` : '';

    const gridLines: ChartGridLine[] = scale.ticks.map(v => ({ y: this.round(yAt(v)), label: this.formatTick(v, metric) }));

    const stepEvery = Math.ceil(n / 8);
    const xLabels: ChartXLabel[] = buckets
      .map((b, i) => ({ x: this.round(xAt(i)), text: b.label, i }))
      .filter(l => l.i % stepEvery === 0)
      .map(({ x, text }) => ({ x, text }));

    return { hasData: true, linePath, areaPath, points, showMarkers: n <= 40, gridLines, xLabels, color };
  });

  public readonly summaryRows = computed<SummaryRow[]>(() => {
    const stats = this.statsData();
    if (!stats) return [];
    const t = stats.totals;
    const int = (n: number) => n.toLocaleString('de-DE');
    const avg = (n: number) =>
      t.deliveryNoteCount > 0 ? (n / t.deliveryNoteCount).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : null;

    // Cells: Rollen | Stck a' | Gesamt Stk. | Folie 1 | Folie A | Folie N | Dry Packs | Ammo Packs | Umkartons | Kt. | kg | Durchschn. pro LZ
    return [
      {
        label: 'Positionen gesamt',
        icon: 'format_list_numbered',
        cells: [
          int(t.rollCount),
          null,
          int(t.positionCount),
          int(t.folie1),
          int(t.folieA),
          int(t.folieN),
          int(t.dryPacks),
          int(t.ammoPacks),
          int(t.umCartons),
          int(t.cartonCount),
          this.formatKg(t.weightKg),
          avg(t.positionCount),
        ],
      },
    ];
  });

  // --- Event handlers ---
  public readonly dayValue = computed(() => this.anchor());

  public onDayPicked(date: Date | null): void {
    if (date) this.anchor.set(date);
  }

  public setMode(mode: PeriodMode): void {
    if (this.mode() === mode) return;
    this.anchor.set(new Date());
    if (mode === 'custom') this.customRange.set(null); // wait until the user picked a complete range
    this.mode.set(mode);
  }

  public shift(direction: 1 | -1): void {
    const current = this.anchor();
    const y = current.getFullYear();
    const m = current.getMonth();
    switch (this.mode()) {
      case 'day': {
        const d = new Date(current);
        d.setDate(d.getDate() + direction);
        this.anchor.set(d);
        break;
      }
      case 'month':
        this.anchor.set(new Date(y, m + direction, 1));
        break;
      case 'year':
        this.anchor.set(new Date(y + direction, 0, 1));
        break;
      case 'custom':
        return;
    }
  }

  public reload(): void {
    this.statsResource.reload();
    this.timelineResource.reload();
  }

  private formatKg(n: number): string {
    return `${n.toLocaleString('de-DE', { maximumFractionDigits: 2 })} kg`;
  }

  private formatTick(value: number, metric: TimelineMetric): string {
    const fractionDigits = metric === 'weightKg' && value % 1 !== 0 ? 1 : 0;
    return value.toLocaleString('de-DE', { maximumFractionDigits: fractionDigits });
  }

  private computeRange(mode: PeriodMode, anchor: Date, customRange: Date[] | null): DateRange | null {
    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    switch (mode) {
      case 'day': {
        const from = new Date(anchor);
        from.setHours(0, 0, 0, 0);
        const to = new Date(anchor);
        to.setHours(23, 59, 59, 999);
        return { from, to };
      }
      case 'month':
        return { from: new Date(y, m, 1), to: new Date(y, m + 1, 0, 23, 59, 59, 999) };
      case 'year':
        return { from: new Date(y, 0, 1), to: new Date(y, 11, 31, 23, 59, 59, 999) };
      case 'custom': {
        if (!customRange || !customRange[0] || !customRange[1]) return null;
        const from = new Date(customRange[0]);
        from.setHours(0, 0, 0, 0);
        const to = new Date(customRange[1]);
        to.setHours(23, 59, 59, 999);
        return { from, to };
      }
    }
  }

  private round(n: number): number {
    return Math.round(n * 100) / 100;
  }

  /** Round a number to a "nice" 1/2/5 × 10ⁿ value. */
  private niceNum(range: number, roundResult: boolean): number {
    const exponent = Math.floor(Math.log10(range));
    const fraction = range / Math.pow(10, exponent);
    let niceFraction: number;
    if (roundResult) {
      if (fraction < 1.5) niceFraction = 1;
      else if (fraction < 3) niceFraction = 2;
      else if (fraction < 7) niceFraction = 5;
      else niceFraction = 10;
    } else {
      if (fraction <= 1) niceFraction = 1;
      else if (fraction <= 2) niceFraction = 2;
      else if (fraction <= 5) niceFraction = 5;
      else niceFraction = 10;
    }
    return niceFraction * Math.pow(10, exponent);
  }

  /** Build an axis scale with round tick values from 0 up to a nice maximum that fits the data. */
  private niceScale(max: number, tickCount: number): { niceMax: number; ticks: number[] } {
    if (max <= 0) return { niceMax: 1, ticks: [0, 1] };
    const step = this.niceNum(this.niceNum(max, false) / tickCount, true);
    const niceMax = Math.ceil(max / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= niceMax + step / 2; v += step) ticks.push(this.round(v));
    return { niceMax, ticks };
  }
}
